// src/components/certificacion/CertificacionIniciar.jsx
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl,
  InputLabel, FormGroup, FormControlLabel, Checkbox,
  Box, Typography, Alert, CircularProgress, Divider,
} from '@mui/material';
import { certificacionService, TIPOS_DTE_CERTIFICACION, TIPOS_ONBOARDING } from '../../services/certificacionService';
import { limpiarRut } from '../../utils/rutValidator';

const TIPOS_BASICOS = [33, 56, 61];

export default function CertificacionIniciar({ open, onClose, onCreated }) {
  const [rutEmpresa, setRutEmpresa] = useState('');
  const [tipoOnboarding, setTipoOnboarding] = useState('nueva');
  const [tiposDte, setTiposDte] = useState(new Set(TIPOS_BASICOS));
  const [numSetBasico, setNumSetBasico] = useState('');
  const [numLibroVentas, setNumLibroVentas] = useState('');
  const [numLibroCompras, setNumLibroCompras] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleToggleTipo = (codigo) => {
    setTiposDte(prev => {
      const next = new Set(prev);
      next.has(codigo) ? next.delete(codigo) : next.add(codigo);
      return next;
    });
  };

  const handleSubmit = async () => {
    setError(null);
    const { rut: rutNum, dv } = limpiarRut(rutEmpresa);
    if (!rutNum || !dv) {
      setError('RUT inválido');
      return;
    }
    const rutFull = `${rutNum}-${dv}`;
    const tiposArray = Array.from(tiposDte);
    if (tiposArray.length === 0) {
      setError('Seleccione al menos un tipo DTE');
      return;
    }

    const numerosAtencion = (tipoOnboarding === 'nueva' || tipoOnboarding === 'parcial')
      ? {
          set_basico: numSetBasico || undefined,
          ...(tipoOnboarding === 'nueva' && {
            libro_ventas: numLibroVentas || undefined,
            libro_compras: numLibroCompras || undefined,
          }),
        }
      : null;

    setLoading(true);
    try {
      await certificacionService.iniciar(rutFull, tipoOnboarding, tiposArray, numerosAtencion);
      onCreated();
      onClose();
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('409') || msg.includes('activa')) {
        setError('Ya existe una certificación activa para esta empresa.');
      } else {
        setError(msg || 'Error al iniciar certificación');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError(null);
    onClose();
  };

  const mostrarNumerosAtencion = tipoOnboarding === 'nueva' || tipoOnboarding === 'parcial';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Iniciar certificación DTE</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="RUT empresa"
            value={rutEmpresa}
            onChange={e => setRutEmpresa(e.target.value)}
            placeholder="77897122-4"
            size="small"
            fullWidth
          />

          <FormControl size="small" fullWidth>
            <InputLabel>Tipo de onboarding</InputLabel>
            <Select
              value={tipoOnboarding}
              label="Tipo de onboarding"
              onChange={e => setTipoOnboarding(e.target.value)}
            >
              {TIPOS_ONBOARDING.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Tipos DTE a certificar
            </Typography>
            <FormGroup row>
              {TIPOS_DTE_CERTIFICACION.map(t => (
                <FormControlLabel
                  key={t.codigo}
                  control={
                    <Checkbox
                      checked={tiposDte.has(t.codigo)}
                      onChange={() => handleToggleTipo(t.codigo)}
                      size="small"
                    />
                  }
                  label={`T${t.codigo} ${t.nombre}`}
                  sx={{ width: '50%' }}
                />
              ))}
            </FormGroup>
          </Box>

          {mostrarNumerosAtencion && (
            <>
              <Divider />
              <Typography variant="body2" color="text.secondary">
                Números de atención asignados por el SII
              </Typography>
              <TextField
                label="Set básico (N° atención)"
                value={numSetBasico}
                onChange={e => setNumSetBasico(e.target.value)}
                size="small"
                fullWidth
                placeholder="4782753"
              />
              {tipoOnboarding === 'nueva' && (
                <>
                  <TextField
                    label="Libro de ventas (N° atención)"
                    value={numLibroVentas}
                    onChange={e => setNumLibroVentas(e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Libro de compras (N° atención)"
                    value={numLibroCompras}
                    onChange={e => setNumLibroCompras(e.target.value)}
                    size="small"
                    fullWidth
                  />
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Iniciar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
